import { useTheme } from '@mui/material';
import {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import type { DateTime } from 'luxon';
import { match } from 'ts-pattern';
import { useHistory } from 'react-router';
import { useGetTheme } from '../../../../hooks/use-get-theme';
import { useGetLoginConfig } from '../../../../hooks/useGetLoginConfig';
import { LoginMethod } from '../../../../selectors/interfaces';
import {
  VerifyUserInputLoginResponseMessage,
  usePostVerifyUserInputLogin,
} from '../../../../hooks/API/use-post-verify-user-input-login';
import { useGetIssuerShortname } from '../../../../hooks/use-get-issuer-shortname';
import { GlobalTextVariablesContext } from '../../../../dotcms/contexts/global-text-variables-context';
import { ResponseMessageFromServer } from '../../../../utils/constants';
import { useGetRouteToIssuerPage } from '../../../../app/get-route-to-page';
import { Page } from '../../../../app/page-url-path';
import useGetPeriodsOpenInfo from '../../../../hooks/use-get-periods-info/use-get-periods-open-info';
import UrlSearchParamKey from '../../../../utils/url-search-params/url-search-params';
import { useStringUrlSearchParam } from '../../../../utils/url-search-params/use-query-url-search-params';
import { useSettingBasedOnBreakpoints } from '../../../../hooks/use-setting-based-on-breakpoints';
import { LoginFormDobGqlHelper } from '../../../../dotcms/dotcms-models/dotcms-login-form-dob';
import { useBuildersToProcessGqlData } from '../../../../dotcms/use-builders-to-process-gql-data';
import { useFetchIssuerDotcmsContentWithDefaultFallback } from '../../../../dotcms/use-fetch-from-dotcms-with-default-fallback';
import { GlobalLoadingStateOperation } from '../../../../components/abc/global-loading-context/global-loading-context';

export const LoginMethodsWithDob = [LoginMethod.BUSINESS_GROUP];

export const AllowedInternalLoginMethods = LoginMethodsWithDob;

const useLoginDobForm = (loginMethod: LoginMethod) => {
  const theme = useGetTheme();
  const muiTheme = useTheme();
  const { isMobile, isTabletMini } = useSettingBasedOnBreakpoints(muiTheme);

  const { createGqlQueryForSingle, processGqlDataAsSingle, contentTypeName } =
    useBuildersToProcessGqlData(LoginFormDobGqlHelper);
  const { data, loading, error } =
    useFetchIssuerDotcmsContentWithDefaultFallback({
      buildGqlSubQuery: createGqlQueryForSingle,
      processResponse: processGqlDataAsSingle,
      loadingStateOperationName:
        GlobalLoadingStateOperation.LOGIN_FORM_DOB_DOTCMS_FETCH,
      contentTypeName,
    });

  const issuerShortName = useGetIssuerShortname();
  const { isEnrollmentOpenDuringOfferingPeriod } = useGetPeriodsOpenInfo();

  const history = useHistory();
  const routeToHomePage = useGetRouteToIssuerPage(Page.HOMEPAGE);
  const redirectTo = useStringUrlSearchParam(UrlSearchParamKey.REDIRECT_TO);

  const { getLoginMethodConfig } = useGetLoginConfig();
  const { encryptedDataDisclaimerText } = useContext(
    GlobalTextVariablesContext,
  );

  const [dob, setDob] = useState<DateTime | null>(null);
  const [dobValidationError, setDobValidationError] = useState<string | null>(
    null,
  );
  const dobError = useMemo(
    () => !dob || !!dobValidationError,
    [dob, dobValidationError],
  );

  const [organization, setOrganization] = useState<string | undefined>(
    undefined,
  );
  const organizationOptions =
    loginMethod === LoginMethod.BUSINESS_GROUP
      ? getLoginMethodConfig(
        LoginMethod.BUSINESS_GROUP,
        true,
      )?.business_groups?.map((x) => ({ name: x, value: x })) ?? []
      : [];
  const organizationError = useMemo(() => !organization, [organization]);

  const [employeeId, setEmployeeId] = useState<string | undefined>(undefined);
  const employeeIdError = useMemo(() => !employeeId, [employeeId]);

  const [last4, setLast4] = useState<string | undefined>(undefined);
  const last4Error = useMemo(() => !last4 || last4.length !== 4, [last4]);

  const disableSubmitTimeoutAfterLockedErrorInMs = 2000;
  const [disableSubmit, setDisableSubmit] = useState(false);

  const [showValidationErrors, setShowValidationErrors] =
    useState<boolean>(false);

  const validationErrors = useMemo(
    () =>
      loginMethod === LoginMethod.BUSINESS_GROUP &&
      (dobError || organizationError || employeeIdError),
    [loginMethod, dobError, organizationError, employeeIdError],
  );

  const {
    sendRequestForBusinessGroup,
    updateDataAfterRequest,
    data: sendRequestData,
    loading: sendRequestLoading,
    error: sendRequestError,
  } = usePostVerifyUserInputLogin();

  const redirectAfterLogin = useCallback(() => {
    updateDataAfterRequest();
    history.push(redirectTo ?? routeToHomePage);
  }, [updateDataAfterRequest, history, routeToHomePage, redirectTo]);

  const [showUnableToVerifyPopup, setShowUnableToVerifyPopup] = useState(false);
  const closeUnableToVerifyPopup = useCallback(
    () => setShowUnableToVerifyPopup(false),
    [],
  );

  const [showUnableToEligibilityPopup, setShowUnableToEligibilityPopup] =
    useState(false);
  const closeUnableToEligibilityPopup = useCallback(
    () => setShowUnableToEligibilityPopup(false),
    [],
  );

  const [showAlreadyEnrolledPopup, setShowAlreadyEnrolledPopup] =
    useState(false);
  const closeAlreadyEnrolledPopup = redirectAfterLogin;

  const [showGenericError, setShowGenericError] = useState(false);

  const handleLoginSubmit = useCallback(() => {
    setShowValidationErrors(true);
    if (!validationErrors) {
      setShowGenericError(false);
      if (
        loginMethod === LoginMethod.BUSINESS_GROUP &&
        dob &&
        issuerShortName &&
        organization &&
        employeeId
      ) {
        sendRequestForBusinessGroup({
          dateOfBirth: dob,
          companyShortName: issuerShortName,
          businessGroup: organization,
          employeeCompanyId: employeeId,
        });
      }
    }
  }, [
    validationErrors,
    dob,
    employeeId,
    issuerShortName,
    loginMethod,
    organization,
    sendRequestForBusinessGroup,
  ]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    match(sendRequestError?.messageFromResponse)
      .with(undefined, () => {})
      .with(VerifyUserInputLoginResponseMessage.VERIFY_UNABLE, () =>
        setShowUnableToVerifyPopup(true))
      .with(
        VerifyUserInputLoginResponseMessage.INVALID_LOGIN_METHOD,
        VerifyUserInputLoginResponseMessage.DENIED_ACCESS,
        () => setShowUnableToEligibilityPopup(true),
      )
      .with(VerifyUserInputLoginResponseMessage.LOGIN_LOCKED, () => {
        setDisableSubmit(true);
        timeoutId = setTimeout(
          () => setDisableSubmit(false),
          disableSubmitTimeoutAfterLockedErrorInMs,
        );
        setShowGenericError(true);
      })
      .with(VerifyUserInputLoginResponseMessage.GENERIC_ERROR, () =>
        setShowGenericError(true))
      .otherwise(() => setShowGenericError(true));

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [sendRequestError?.messageFromResponse]);

  useEffect(() => {
    if (sendRequestData && !sendRequestError) {
      match(sendRequestData.message)
        .with(
          VerifyUserInputLoginResponseMessage.VALIDATE_EMAIL,
          VerifyUserInputLoginResponseMessage.EXACT_MATCH,
          ResponseMessageFromServer.SUCCESS,
          () => {
            // TODO: Extract information if a user can make changes during open enrollment.
            const changesDuringOpenEnrollmentAllowed = true;
            return isEnrollmentOpenDuringOfferingPeriod &&
              sendRequestData.information.enrolled &&
              !changesDuringOpenEnrollmentAllowed
              ? setShowAlreadyEnrolledPopup(true)
              : redirectAfterLogin();
          },
        )
        .with(
          VerifyUserInputLoginResponseMessage.MULTIPLE_FOUND,
          VerifyUserInputLoginResponseMessage.ENTER_SSN,
          () => setShowGenericError(true),
        )
        .otherwise(() => setShowGenericError(true));
    }
  }, [
    sendRequestData,
    sendRequestError,
    updateDataAfterRequest,
    redirectAfterLogin,
    isEnrollmentOpenDuringOfferingPeriod,
  ]);

  return {
    theme,
    muiTheme,
    isMobile,
    isTabletMini,
    data,
    loading,
    error,
    dob,
    setDob,
    setDobValidationError,
    dobError,
    organization,
    setOrganization,
    organizationOptions,
    organizationError,
    employeeId,
    setEmployeeId,
    employeeIdError,
    last4,
    setLast4,
    last4Error,
    disableSubmit,
    validationErrors,
    showValidationErrors,
    handleLoginSubmit,
    encryptedDataDisclaimerText,
    sendRequestLoading,
    showUnableToVerifyPopup,
    closeUnableToVerifyPopup,
    showUnableToEligibilityPopup,
    closeUnableToEligibilityPopup,
    showAlreadyEnrolledPopup,
    closeAlreadyEnrolledPopup,
    showGenericError,
  };
};

export default useLoginDobForm;
